import React, { useState } from "react";
import { X, Check, Loader2, Pencil, Trash2, Eye } from "lucide-react";
import { conditionLabel } from "@/lib/marketplace";
import { ListingPhoto } from "@/components/PhotoPlaceholder";

/**
 * InspectListingModal — Iteration 259.
 *
 * VIEW-ONLY inspection modal for the "Ledger & Activity -> Pending
 * Approvals" queue (and the dashboard Unified Pending Queue). Clicking a
 * card's View button / thumbnail / title opens this read-only summary —
 * full gallery, details, fulfilment method and child handle — with the
 * event-specific color-coded action buttons along the bottom.
 *
 * Four variants (exact titles per spec):
 *   new_listing → "Inspect New Listing"
 *   piggy       → "Piggy Bank Purchase Approval"
 *   handover    → "Parent Hand-over Approval"
 *   school      → "At/After School Collection Approval"
 */

export const INSPECT_VARIANTS = {
  new_listing: {
    title: "Inspect New Listing",
    badge: "NEW LISTING",
    badgeClass: "bg-brandTeal/15 text-brandTeal border border-brandTeal/30",
    approveLabel: "Approve Listing",
    rejectLabel: "Reject",
    rejectStyle: "outline",
    showEdit: true,
    showDelete: true,
  },
  piggy: {
    title: "Piggy Bank Purchase Approval",
    badge: "PIGGY BANK PURCHASE",
    badgeClass: "bg-brandPink/15 text-brandPink border border-brandPink/30",
    approveLabel: "Approve Purchase",
    rejectLabel: "Decline & Release",
    rejectStyle: "solid",
    showEdit: false,
    showDelete: false,
  },
  handover: {
    title: "Parent Hand-over Approval",
    badge: "PARENT HAND-OVER",
    badgeClass: "bg-amber-100 text-amber-800 border border-amber-300",
    approveLabel: "Approve Handover",
    rejectLabel: "Decline",
    rejectStyle: "outline",
    showEdit: false,
    showDelete: false,
  },
  school: {
    title: "At/After School Collection Approval",
    badge: "SCHOOL COLLECTION",
    badgeClass: "bg-blue-100 text-blue-800 border border-blue-300",
    approveLabel: "Approve Collection",
    rejectLabel: "Decline",
    rejectStyle: "outline",
    showEdit: false,
    showDelete: false,
  },
};

/** Classify a pending purchase approval row into its modal variant. */
export function inspectVariantForOrder(o) {
  const fm = String(o?.fulfillment_method || o?.delivery_option || "").toLowerCase();
  if (fm.includes("school")) return "school";
  if (/hand|meetup|collection|pickup|pick_up|local/.test(fm)) return "handover";
  return "piggy";
}

const FULFILLMENT_LABELS = {
  parent_handoff: "Parent Handover",
  parent_handover: "Parent Handover",
  handover: "Parent Handover",
  safe_meetup: "Parent Handover",
  school_gate: "At/After School Collection",
  school: "At/After School Collection",
  school_collection: "At/After School Collection",
  local_shop: "Local Shop Pick-Up",
  home_delivery: "Royal Mail Post",
  shipping: "Royal Mail Post",
  royal_mail: "Royal Mail Post",
};

export function fulfillmentLabel(fm) {
  if (!fm) return null;
  const key = String(fm).toLowerCase().trim();
  return FULFILLMENT_LABELS[key] || null;
}

function DetailRow({ label, value, testid }) {
  if (!value) return null;
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-slate-100 last:border-0">
      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex-shrink-0">{label}</span>
      <span className="text-sm font-semibold text-slate-800 text-right break-words" data-testid={testid}>{value}</span>
    </div>
  );
}

export default function InspectListingModal({
  variant = "new_listing",
  details = {},
  busy = false,
  onClose,
  onApprove,
  onReject,
  onEdit,
  onDelete,
}) {
  const v = INSPECT_VARIANTS[variant] || INSPECT_VARIANTS.new_listing;
  const photos = Array.isArray(details.photos) ? details.photos.filter(Boolean) : [];
  const [activePhoto, setActivePhoto] = useState(0);
  const mainPhoto = photos[Math.min(activePhoto, photos.length - 1)] || null;

  const rejectClasses = v.rejectStyle === "solid"
    ? "bg-rose-600 hover:bg-rose-700 text-white border border-rose-600"
    : "bg-white border border-rose-200 hover:border-rose-400 text-rose-700 hover:bg-rose-50";

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center px-4 py-6"
      data-testid="inspect-modal"
      role="dialog"
      aria-modal="true"
      aria-label={v.title}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="bg-white rounded-3xl w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3 p-5 pb-0">
          <div className="min-w-0">
            <span className={`pill text-[10px] ${v.badgeClass}`} data-testid="inspect-modal-badge">{v.badge}</span>
            <h3 className="font-display text-xl md:text-2xl font-medium text-slate-900 mt-1.5" data-testid="inspect-modal-title">
              {v.title}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 transition flex-shrink-0"
            data-testid="inspect-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Gallery */}
          <div data-testid="inspect-gallery">
            <ListingPhoto
              src={mainPhoto}
              alt={details.title || "Listing photo"}
              className="w-full h-56 rounded-2xl object-cover border border-slate-200"
              testId="inspect-photo-main"
            />
            {photos.length > 1 && (
              <div className="flex gap-2 mt-2 overflow-x-auto" data-testid="inspect-gallery-thumbs">
                {photos.map((p, i) => (
                  <button
                    key={`${p}-${i}`}
                    type="button"
                    onClick={() => setActivePhoto(i)}
                    className={`flex-shrink-0 rounded-xl border-2 transition ${i === activePhoto ? "border-brandSlate" : "border-transparent opacity-70 hover:opacity-100"}`}
                    data-testid={`inspect-photo-thumb-${i}`}
                    aria-label={`Photo ${i + 1}`}
                  >
                    <img src={p} alt="" className="w-14 h-14 rounded-lg object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Title / price / condition */}
          <div>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="font-display text-lg font-medium text-slate-900 break-words" data-testid="inspect-title">
                {details.title || "Listing"}
              </div>
              {details.priceLabel && (
                <div className="text-lg font-bold text-brandSlate" data-testid="inspect-price">{details.priceLabel}</div>
              )}
            </div>
            {details.condition && (
              <span className="pill bg-slate-100 text-slate-700 border border-slate-200 text-[10px] mt-1 inline-block" data-testid="inspect-condition">
                {conditionLabel(details.condition)}
              </span>
            )}
          </div>

          {details.description && (
            <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line break-words" data-testid="inspect-description">
              {details.description}
            </p>
          )}

          {/* Context rows */}
          <div className="rounded-2xl bg-slate-50 border border-slate-100 px-4 py-1" data-testid="inspect-details">
            <DetailRow label="Fulfilment" value={details.fulfillmentLabel} testid="inspect-fulfillment" />
            <DetailRow label="Buyer" value={details.buyerLine} testid="inspect-buyer" />
            <DetailRow label="Seller" value={details.sellerLine} testid="inspect-seller" />
            <DetailRow label="Listed by" value={details.listedByLine} testid="inspect-child-handle" />
          </div>

          {/* Action buttons — event-specific wording + colors */}
          <div className="flex flex-wrap gap-2 pt-1" data-testid="inspect-actions">
            {onApprove && (
              <button
                type="button"
                onClick={onApprove}
                disabled={busy}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold shadow-sm transition disabled:opacity-60"
                data-testid="inspect-approve"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {v.approveLabel}
              </button>
            )}
            {v.showEdit && onEdit && (
              <button
                type="button"
                onClick={onEdit}
                disabled={busy}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 text-sm font-bold transition disabled:opacity-60"
                data-testid="inspect-edit"
              >
                <Pencil className="w-4 h-4" /> Edit
              </button>
            )}
            {onReject && (
              <button
                type="button"
                onClick={onReject}
                disabled={busy}
                className={`inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-bold transition disabled:opacity-60 ${rejectClasses}`}
                data-testid="inspect-reject"
              >
                <X className="w-4 h-4" /> {v.rejectLabel}
              </button>
            )}
            {v.showDelete && onDelete && (
              <button
                type="button"
                onClick={onDelete}
                disabled={busy}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-rose-700 hover:bg-rose-800 text-white text-sm font-bold transition disabled:opacity-60"
                data-testid="inspect-delete"
              >
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            )}
          </div>
          <p className="text-[11px] text-slate-400 flex items-center gap-1">
            <Eye className="w-3 h-3" /> Read-only preview — use the buttons above to take action.
          </p>
        </div>
      </div>
    </div>
  );
}
